// Paso 6 del asistente (PE-A3b; PDF y versiones en PE-B1, pe-plan-pdf.ts): vista previa en pantalla. Estructura del plan
// según PASO0_DISENO.md §4.3 (secciones 1 a 17, firmas y anexo), llenada con los datos de los pasos 1 a 5.
// Reglas: lo que no tiene datos dice "pendiente"; los por validar se marcan; las firmas y el sello de los
// bomberos: las firmas se toman al aprobar la versión y el sello queda reservado (PE-26). Textos base solo desde DE 255, AM 1257 y RTQ
// (PE-33): como aún no hay textos base transcritos y revisados por Kevin, esas partes dicen
// "pendiente de texto base" y no se inventan.
// PE-A4: sección 18, continuidad del negocio (DE 394, Art. 5 núm. 3; vacía o incompleta = por validar, va al anexo de
// pendientes sin impedir aprobar) y 19, transferencia de riesgos (Art. 5 núm. 5, informativa).

import type { FilaAccion } from './pe-acciones';
import { ETIQUETA_ESTADO_ACCION, origenResuelto } from './pe-acciones';
import type { FilaPlan } from './pe-datos';
import type { SectorEnPantalla } from './pe-paso2';
import type { Evaluacion } from './pe-paso3';
import { sectoresPorQs } from './pe-resultados';
import { etiquetaNivel, Nivel4 } from './pe-constantes-plan-referencia';
import type { EstadoTraza } from './pe-tipos';
import { formatoEc } from './pe-unidades';
import { plural } from '../plurales';
import { continuidadDe, lineasContinuidad, lineasTransferencia, transferenciaDe, trazasContinuidad } from './pe-continuidad';

export const TITULO_CONTINUIDAD = 'Continuidad del negocio (DE 394, Art. 5 núm. 3)';
export const TITULO_TRANSFERENCIA = 'Transferencia de riesgos (DE 394, Art. 5 núm. 5, cuando corresponda)';

// La continuidad guardada en el plan (M37); null si no hay nada.
export const continuidadDelPlan = (p: FilaPlan | null) => (p?.business_continuity ? continuidadDe(p.business_continuity) : null);

export const PENDIENTE = 'Pendiente';
export const PENDIENTE_TEXTO_BASE = 'Pendiente de texto base (DE 255, AM 1257 y RTQ)';
export const MARCA_DE_AGUA = 'BORRADOR — vista previa';

export interface Linea { texto: string; estado?: EstadoTraza; pendiente?: boolean }
export interface SeccionVista { numero: string; titulo: string; lineas: Linea[]; porValidar: number }

export interface DatosVista {
  empresa: string;
  plan: FilaPlan | null;
  sectores: SectorEnPantalla[];
  evaluaciones: Evaluacion[];
  acciones: FilaAccion[];
  brigadaMiembros: number | null;
  hoy?: Date;
}

const pend = (texto = PENDIENTE): Linea => ({ texto, pendiente: true });
const base = (): Linea => ({ texto: PENDIENTE_TEXTO_BASE, pendiente: true });

// PE-A4b: "apto" solo con todas las eliminatorias respondidas y ninguna en "no"; si falta alguna, "por validar".
export function lineaPuntoEncuentro(pe: Evaluacion | undefined): Linea {
  if (pe?.resumen.descartado === true) return { texto: 'Punto de encuentro: NO APTO', estado: 'no_cumple' };
  if (pe?.resumen.descartado !== false) return { texto: `Punto de encuentro: ${PENDIENTE}`, pendiente: true };
  const faltan = (pe.resumen.eliminatoriasSinResponder ?? []) as string[];
  if (faltan.length) {
    return { texto: `Punto de encuentro: por validar; ${faltan.length === 1 ? 'falta la condición eliminatoria' : `faltan ${faltan.length} condiciones eliminatorias`}: ${faltan.join(', ')} (se completa en el capítulo 6)`, estado: 'por_validar' };
  }
  return { texto: 'Punto de encuentro: apto según las condiciones' };
}

export function armarVistaPrevia(d: DatosVista): SeccionVista[] {
  const ev = (clave: string) => d.evaluaciones.find(e => e.clave === clave);
  const pv = (claves: string[]) => d.evaluaciones.filter(e => claves.some(c => e.clave.startsWith(c)))
    .reduce((a, e) => a + e.trazas.filter(t => t.estado === 'por_validar').length, 0);
  const p = d.plan;
  const hoy = d.hoy ?? new Date();
  const sec = (numero: string, titulo: string, lineas: Linea[], claves: string[] = []): SeccionVista =>
    ({ numero, titulo, lineas: lineas.length ? lineas : [pend()], porValidar: pv(claves) });

  const s = sectoresPorQs(d.sectores);
  const meseri = d.evaluaciones.filter(e => e.metodo === 'meseri');
  const ver = ev('verificacion');
  const af = ev('aforo_egreso');
  const rs = ev('rset');
  const pe = ev('punto_encuentro');
  const am = ev('amenaza_vulnerabilidad');
  const amenazas = am ? Object.values(am.resumen) as { nombre: string; amenaza: Nivel4; vulnerabilidad: Nivel4; riesgo: Nivel4; porcentaje: number }[] : [];
  const noCumple = (e: Evaluacion | undefined): Linea[] => (e?.trazas ?? []).filter(t => t.estado === 'no_cumple')
    .map(t => ({ texto: `${t.articulo_o_tabla} (${t.norma}): ${t.nota ?? ''}`.trim(), estado: 'no_cumple' }));
  // PE-A3c: una acción con "origen resuelto" ya no es un pendiente: en la sección 5 se marca "resuelta por
  // cambio de origen" y el cronograma (17) no la programa; solo dice cuántas quedaron así.
  const vigentes = new Set(d.evaluaciones.flatMap(e => e.acciones.map(x => x.sourceKey)));
  const resuelta = (a: FilaAccion) => origenResuelto(a, vigentes);
  const accionesTxt = d.acciones.map(a => ({
    texto: resuelta(a)
      ? `${a.description} · resuelta por cambio de origen`
      : `${a.description} · ${ETIQUETA_ESTADO_ACCION[a.status]}${a.due_date ? ` · hasta ${a.due_date}` : ''}`,
  }));
  const programadas = d.acciones.filter(a => a.due_date && !resuelta(a)).sort((a, b) => (a.due_date as string).localeCompare(b.due_date as string));
  const resueltas = d.acciones.filter(resuelta).length;

  return [
    sec('Portada', 'Portada', [
      { texto: `Empresa: ${d.empresa || PENDIENTE}` },
      { texto: `Código: ${p?.code ?? PENDIENTE}`, pendiente: !p?.code },
      { texto: `Versión: ${(p?.current_version ?? 0) + 1} (borrador)` },
      { texto: `Mes y año: ${hoy.toLocaleDateString('es-EC', { month: 'long', year: 'numeric' })}` },
      pend('Representante legal y responsable de la elaboración'),
    ]),
    sec('1', 'Descripción general de la empresa', [
      { texto: `Cantón: ${p?.site_canton ?? PENDIENTE}${p?.site_province ? ` (${p.site_province})` : ''}`, pendiente: !p?.site_canton },
      { texto: `Superficie total: ${p?.site_area_m2 != null ? `${formatoEc(p.site_area_m2, 2)} m²` : PENDIENTE}`, pendiente: p?.site_area_m2 == null },
      { texto: `Trabajadores: ${p?.site_workers ?? PENDIENTE}`, pendiente: p?.site_workers == null },
      { texto: `Ocupación: ${p?.site_occupancy ?? PENDIENTE}`, pendiente: !p?.site_occupancy },
      pend('Actividad principal, horario y visitantes (1.1)'), base(),
    ]),
    sec('2', 'Identificación de factores de riesgo', [
      ...d.sectores.map(x => ({ texto: `Área ${x.sector.name}: ${formatoEc(x.sector.area_m2, 2)} m², ${x.sector.occupancy}` })),
      pend('Fotos por área y límites (norte, sur, este, oeste)'),
      ...amenazas.map(a => ({ texto: `Amenaza natural: ${a.nombre} (amenaza ${etiquetaNivel(a.amenaza, 'f')})` })),
    ], ['amenaza_vulnerabilidad']),
    sec('3', 'Evaluación de riesgos detectados', [...amenazas.map(a => ({ texto: `${a.nombre}: riesgo ${etiquetaNivel(a.riesgo, 'm')}` })), base()], ['amenaza_vulnerabilidad']),
    sec('4', 'Medición y evaluación de los riesgos', [
      ...s.filas.map(f => ({ texto: f.error ? `Carga de fuego · ${f.nombre}: ${f.error}` : `4.1 Carga de fuego · ${f.nombre}: Qs ${formatoEc(f.qsMJm2, 1)} MJ/m², nivel ${f.nivel} RD 164, ${f.art139} (Art. 139)`, estado: f.error ? 'por_validar' as const : undefined })),
      ...meseri.map(m => ({ texto: m.resumen.p !== undefined ? `4.2 ${m.titulo}: P = ${formatoEc(m.resumen.p as number, 2)} (${String(m.resumen.cualitativa)})` : `4.2 ${m.titulo}: ${PENDIENTE}`, pendiente: m.resumen.p === undefined })),
      { texto: `4.3 Priorización por Qs: ${s.filas.filter(f => !f.error).map(f => f.nombre).join(' > ') || PENDIENTE} (en revisión)` },
      ...(noCumple(ver).length ? noCumple(ver).map(l => ({ ...l, texto: `4.4 ${l.texto}` })) : [{ texto: '4.4 Verificación normativa: sin incumplimientos registrados' }]),
      { texto: `4.5 Aforo: ${typeof af?.resumen.aforo === 'number' ? `${af.resumen.aforo} personas` : PENDIENTE}`, pendiente: typeof af?.resumen.aforo !== 'number' },
    ], ['meseri', 'verificacion', 'aforo_egreso']),
    sec('5', 'Acciones preventivas y de control', [...accionesTxt, base()]),
    sec('6', 'Riesgo de amenazas naturales', amenazas.map(a => ({
      texto: `${a.nombre}: amenaza ${etiquetaNivel(a.amenaza, 'f')} × vulnerabilidad ${etiquetaNivel(a.vulnerabilidad, 'f')} (${formatoEc(a.porcentaje, 1)} %) = riesgo ${etiquetaNivel(a.riesgo, 'm')}`,
    })).concat([base()]), ['amenaza_vulnerabilidad']),
    sec('7', 'Prevención y control', [
      ...(ver?.trazas ?? []).filter(t => ['Art. 120', '15.4', 'Art. 23', '9.1 y 9.2.1'].includes(t.articulo_o_tabla))
        .map(t => ({ texto: `${t.articulo_o_tabla} (${t.norma}): ${t.nota ?? ''}`, estado: t.estado })),
      pend('Recursos por área (extintores con capacidad y agente, lámparas, detectores): registro de equipos, próximamente'), // Fase C
    ], ['verificacion']),
    sec('8', 'Mantenimiento de los recursos', [
      ...(ver?.trazas ?? []).filter(t => t.articulo_o_tabla === 'Art. 32 d)').map(t => ({ texto: `Prueba hidrostática: ${t.nota ?? ''}`, estado: t.estado })),
      base(),
    ], ['verificacion']),
    sec('9', 'Protocolo de alarma y comunicaciones', [base()]),
    sec('10', 'Grados de emergencia', [base(), pend('Diagrama de actuación por grado')]),
    sec('11', 'Disposiciones generales', [base()]),
    sec('12', 'Protocolo de intervención', [
      { texto: `12.1 Brigada: ${d.brigadaMiembros === null ? PENDIENTE : plural(d.brigadaMiembros, 'persona', 'personas')} (nombres, cargos y roles: pendiente)`, pendiente: d.brigadaMiembros === null },
      { texto: '12.2 Teléfono de emergencias: 911' }, pend('12.2 Cartilla de organismos (distancia, dirección, tiempo)'), base(),
    ]),
    sec('13', 'Evacuación', [base(), pend('Mapa de riesgos, recursos y evacuación (anexo)')]),
    sec('14', 'Evaluación del punto de encuentro', [
      { texto: `14.1 Tiempo de salida (Togawa): ${typeof rs?.resumen.tsSegundos === 'number' ? `${formatoEc(rs.resumen.tsSegundos, 1)} s (${formatoEc(rs.resumen.tsMinutos as number, 2)} min)` : PENDIENTE}`, pendiente: typeof rs?.resumen.tsSegundos !== 'number' },
      { texto: `14.2 RSET: ${typeof rs?.resumen.rsetSegundos === 'number' ? `${formatoEc(rs.resumen.rsetSegundos, 1)} s` : 'falta el tiempo de premovimiento'}`, estado: typeof rs?.resumen.rsetSegundos === 'number' ? undefined : 'por_validar' },
      lineaPuntoEncuentro(pe),
      ...noCumple(pe),
    ], ['rset', 'punto_encuentro']),
    sec('15', 'Implantación del plan', [base(), pend('Simulacros programados (Calendario)')]),
    sec('16', 'Actuación especial', [base()]),
    sec('17', 'Cronograma de implementación', programadas.map(a => ({ texto: `${a.due_date}: ${a.description}` }) as Linea)
      .concat(programadas.length ? [] : [pend()])
      .concat(resueltas ? [{ texto: `${plural(resueltas, 'acción resuelta', 'acciones resueltas')} por cambio de origen: no se programan (ver sección 5)` }] : [])),
    { numero: '18', titulo: TITULO_CONTINUIDAD, porValidar: trazasContinuidad(continuidadDelPlan(p)).length,
      lineas: lineasContinuidad(continuidadDelPlan(p)).map(l => ({ texto: l.texto, estado: l.porValidar ? 'por_validar' as const : undefined })) },
    sec('19', TITULO_TRANSFERENCIA, lineasTransferencia(transferenciaDe(p), hoy).map(t => ({ texto: t, estado: 'informativo' as const }))),
    sec('Firmas', 'Firmas', [
      pend('Elaborado por (responsable de SST): se firma en pantalla al aprobar la versión'),
      pend('Representante legal: se firma en pantalla al aprobar la versión'),
      pend('Aprobación del Cuerpo de Bomberos: espacio para el sello'),
    ]),
    sec('Anexo', 'Anexo: mapa de riesgos, recursos y evacuación', [pend('Imagen subida por el responsable')]),
  ];
}
